import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useLockStore } from '../../stores/lockStore.js';
import { LockScreen } from './LockScreen.js';
import { useAdminLockActive } from './useAdminLock.js';

/**
 * Layer 2 — the admin PIN, in front of the console's SYSTEM surfaces.
 *
 * It used to be in front of all of Settings. The owner narrowed it — "i want
 * this for apk and system settings not for everything" — so what renders this
 * now is the device register and the three system settings groups named in
 * `settingsCatalog`'s `adminLockedGroups.ts`, which carries the reasoning and
 * is the only list. Pay rules, leave, letters and the company address are past
 * the login and the ADMIN/HR role check like everything else, with no second
 * keypad.
 *
 * Re-locks on reload (settingsUnlocked isn't persisted).
 */
export function AdminLockGate({ children }: { children: ReactNode }): JSX.Element {
  const { t } = useTranslation();
  const locked = useAdminLockActive();
  const unlockSettings = useLockStore((s) => s.unlockSettings);

  if (locked) {
    return (
      <LockScreen
        scope="admin"
        title={t('lock.admin.title', 'Admin PIN')}
        subtitle={t(
          'lock.admin.subtitle',
          'Devices and the system settings are protected — enter the admin PIN'
        )}
        onUnlock={unlockSettings}
      />
    );
  }
  return <>{children}</>;
}
